import { Fragment, Component, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation, withTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router";
import { RestAPI } from "@aws-amplify/api-rest";
import { withFormik, Form } from "formik";
import LoadingDialog from "../Utils/LoadingDialog";
import SelfHelp from "../Utils/SelfHelp";
import { callSend, FormRow, HeaderButtons } from "../Form/Form";
import { updateMapInfo, GoogleMapsFarm } from "../GoogleMapsFarm/GoogleMapsFarm";
import { faInfoCircle, faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import "react-toastify/dist/ReactToastify.css";
import { FarmContext } from "../../contexts/FarmContext";
import { dateFormat } from "../../utils/date";
import getBaseLangs from "../../utils/language";
import withName from "../../hoc/withName";
import { SERVICE_DROPCONTROL } from "../../utils/BillingUtils";
import ButtonPrime from "../PrimeComponents/ButtonPrime";
import TooltipPrime from "../PrimeComponents/TooltipPrime";
import switchTipoNodo from "../../utils/switchTipoNodo";
const node = "node_form";

const getNode = (location) => {
    if (location.search.search("node") !== -1) {
        const params = new URLSearchParams(location && location.search);
        const node = params.get("node");
        return node;
    } else {
        return null;
    }
};

const getFarm = (location) => {
    const params = new URLSearchParams(location && location.search);
    const farm = params.get("farm");
    return farm;
};

/** assumes array elements are primitive types
* check whether 2 arrays are equal sets.
* @param  {} a1 is an array
* @param  {} a2 is an array
*/
const areArraysEqualSets = (a1, a2) => {
    const superSet = {};
    for (const i of a1) {
        superSet[i] = 1;
    }

    for (const i of a2) {
        if (!superSet[i]) {
            return false;
        }
        superSet[i] = 2;
    }

    for (const i in superSet) {
        if (superSet[i] === 1) {
            return false;
        }
    }

    return true;
};

const formatPortsData = (data) => {
    const multiDepthSensors = {};
    const multiDepthSensorsP2 = {}; // to handle "port" on c1
    const flowmeters = [];

    const pte = (Array.isArray(data.puertos_tarjetas_expansion) ? data.puertos_tarjetas_expansion : []).reduce(
        (result, { modelo_id, modelo, puerto_nodo, puerto_tarjeta, puerto_tarjeta_expansion_id, sist_tipo_puerto_te }) => {
            if (!puerto_nodo) return result;
            if (!result.hierarchy[puerto_nodo]) {
                if (puerto_nodo > result.maxNPort) result.maxNPort = puerto_nodo;
                result.hierarchy[puerto_nodo] = { modelo, tipo: modelo_id, puertos: [] };
            }

            // do not add virtual sensors (port >=120)
            if (!puerto_tarjeta || puerto_tarjeta >= 120) return result;
            if (!result.hierarchy[puerto_nodo].puertos[puerto_tarjeta]) {
                result.hierarchy[puerto_nodo].puertos[puerto_tarjeta] = { sist_tipo_puerto_te };
            }
            const pte = result.hierarchy[puerto_nodo].puertos[puerto_tarjeta];

            const sensores_puertoTE = Array.isArray(data.sensores) && data.sensores.filter(
                (sensor) => sensor.puerto_tarjeta_expansion_id === puerto_tarjeta_expansion_id);

            Array.isArray(sensores_puertoTE) && sensores_puertoTE.forEach(s => {
                if (!flowmeters.includes(s)) {
                    flowmeters.push(s);
                }
            });

            const puerto = puerto_tarjeta_expansion_id;

            //array with sensors with the same port
            const samePort = [];
            flowmeters.length > 0 && flowmeters.forEach(sensor_puerto_tarjeta_expansion => {
                if (sensor_puerto_tarjeta_expansion.puerto_tarjeta_expansion_id === puerto) {
                    samePort.push(sensor_puerto_tarjeta_expansion);
                }

                if (sensor_puerto_tarjeta_expansion.repeat) return null;
                sensor_puerto_tarjeta_expansion.repeat = true;

                if (sensor_puerto_tarjeta_expansion) {
                    if (sensor_puerto_tarjeta_expansion.profundidad) {
                        const isPort2 = (modelo_id === 20) && puerto_tarjeta >= 65;
                        const dataObject = isPort2 ? multiDepthSensorsP2 : multiDepthSensors;
                        if (!dataObject[puerto_nodo]) dataObject[puerto_nodo] = [];
                        const teData = dataObject[puerto_nodo];

                        const existentSensor = teData.find((s) => {
                            if (s.sist_sensor_interno_id !== sensor_puerto_tarjeta_expansion.sist_sensor_interno_id) return false;
                            if (Array.isArray(s.sectores) !== Array.isArray(sensor_puerto_tarjeta_expansion.sectores)) return false;
                            if (!Array.isArray(s.sectores)) return true; // none have sectores
                            return areArraysEqualSets(s.sectores, sensor_puerto_tarjeta_expansion.sectores);
                        });
                        if (existentSensor) {
                            existentSensor.extraDepths = existentSensor.extraDepths + 1;
                            return result;
                        } else {
                            sensor_puerto_tarjeta_expansion.extraDepths = 0;
                            teData.push(sensor_puerto_tarjeta_expansion);
                        }
                    }

                    pte.sensor = samePort.length > 1 ? samePort : sensor_puerto_tarjeta_expansion;
                    return result;
                }
            });

            const actuador_puerto_tarjeta_expansion = Array.isArray(data.actuadores) && data.actuadores.find(
                (actuador) => actuador.puerto_tarjeta_expansion_id === puerto_tarjeta_expansion_id
            );
            if (actuador_puerto_tarjeta_expansion) pte.actuador = actuador_puerto_tarjeta_expansion;

            return result;
        }, { hierarchy: [], maxNPort: 1, firstIsInternal: false });

    switch (data.tipo) {
        case 1:
            if (pte.maxNPort > 0 && !pte.hierarchy[5]) pte.hierarchy[5] = { modelo: "Modem" };
            // fallthrough
        case 2:
            // x1: 0 + 5
            if (pte.maxNPort !== 5) {
                pte.maxNPort = 5;
            }
            break;
        case 7:
        case 8:
            // c1: 1 + 5
            pte.firstIsInternal = true;
            if (pte.maxNPort > 1) {
                if (!pte.hierarchy[1]) pte.hierarchy[1] = {}; // c1 always shows the first one as internal

                // we add a value to index 0, so values are not moved in the next step
                pte.hierarchy[0] = null;
                pte.hierarchy = pte.hierarchy.filter(() => true); // this removes holes in the array
                pte.maxNPort = pte.hierarchy.length - 1;
                pte.noNumberedCards = true;
            }
            break;
        case 9:
        case 10:
            // m1: 1 + 0
            pte.firstIsInternal = true;
            if (pte.maxNPort !== 1) pte.maxNPort = 1;
            break;
        default:
            break;
    }
    return pte;
};

const getExpansionName = (t, { modelo, tipo }) => {
    return (tipo && t(`tipo.tarjeta_expansion.${tipo}`, modelo)) || modelo;
};

const mapeo = {
    // C1
    20: [
        { i18n: "ports.output", min: 1, max: 10 },
        { i18n: "ports.discreta", min: 11, max: 20 },
        { i18n: "ports.420", min: 21, max: 24 },
        { i18n: "ports.serial", min: 25, max: 64, port: 1 },
        { i18n: "ports.serial", min: 65, max: 104, port: 2 }
    ],
    // 10DI_10Rele
    26: [
        { i18n: "ports.output", min: 1, max: 10 },
        { i18n: "ports.discreta", min: 11, max: 20 }
    ],
    // M
    22: [
        { i18n: "ports.serial", min: 1, max: 40, port: 1 },
        { i18n: "ports.sdi12serial", min: 41, max: 80, port: 1 },
        { i18n: "ports.digital", min: 81, max: 84 },
        { i18n: "ports.420analog", min: 85, max: 86 },
        { i18n: "ports.voltaje", min: 87, max: 88 }
    ],
    // Bornera
    9: [
        { i18n: "ports.discreta", min: 1, max: 20 }
    ],
    // Matriz
    12: [
        { i18n: "ports.output", min: 1, max: 20 },
        { i18n: "ports.discreta", min: 21, max: 30 },
        { i18n: "ports.420", min: 31, max: 34 }
    ]
};

const ExpansionRow = ({ farmId, x1, c1, slot, expansion, expansionGroup, terminal, terminalGroup, port, portGroup,
    showTerminal, actuador, sensor, lng, t, sectorsInfo, actuatorZones, navigate }, key) => {
    let tipo = "-";

    if (!Array.isArray(sensor) && sensor) {
        if(!sensor.tipo){
            tipo = `sensor.${sensor.sensor_id}`;
        }else{
            tipo = sensor.tipo?.[lng?.[0]] || sensor.tipo?.[lng?.[1]] || "-";
        }
    } else if (actuador) {
        tipo = t(`tipo.actuador.${actuador.sist_tipo_actuador_id}`, "-");
        if (Array.isArray(actuador.hidraulicas) && actuador.hidraulicas.length > 0) {
            tipo = `${tipo} - ${t(`tipo.asociacion_ha.${actuador.hidraulicas[0].tipo_asociacion_ha}`, "")}`;
        }
    }
    const extraTipo = (sensor && sensor.extraDepths) ? ` (${sensor.extraDepths + 1} ${t("node.portsTable.profundidades")})` : "";
    const marca_modelo = sensor ? sensor.marca_modelo : actuador &&
    (actuador.marca ? (actuador.modelo ? `${actuador.marca} ${actuador.modelo}` : actuador.marca) : actuador.modelo);
    const sectors = sensor ? sensor.sectores : (actuador && actuatorZones && actuatorZones[actuador.actuador_id]);

    return (<tr key={key}>
        {x1 && expansionGroup > 0 && <td key={0} className="align-middle" rowSpan={expansionGroup}>{slot}</td>}
        {x1 && expansionGroup > 0 && <td key={1} className="align-middle" rowSpan={expansionGroup} colSpan={showTerminal ? 1 : 2}>{expansion}</td>}
        {c1 && expansionGroup > 0 && <td key={0} className="align-middle" rowSpan={expansionGroup}>{expansion}</td>}
        {(!x1 || showTerminal) && terminalGroup > 0 && <td key={2} className="align-middle" rowSpan={terminalGroup}>{t(terminal)}</td>}
        {portGroup > 0 && <td key={3} className="align-middle" rowSpan={/* Array.isArray(sensor) && sensor.length ? sensor.length :  */portGroup}>{port}</td>}
        {Array.isArray(sensor) && sensor.length > 0 ?
            sensor.map((s) => {
                let tipo = "-";
                if(!sensor.tipo){
                    tipo = `sensor.${sensor.sensor_id}`;
                }else{
                    tipo = s.tipo?.[lng?.[0]] || s.tipo?.[lng?.[1]] || "-";
                }
                const extraTipo = (s && s.extraDepths) ? ` (${s.extraDepths + 1} ${t("node.portsTable.profundidades")})` : "";
                const marca_modelo = s ? s.marca_modelo : actuador &&
          (actuador.marca ? (actuador.modelo ? `${actuador.marca} ${actuador.modelo}` : actuador.marca) : actuador.modelo);
                const sectors = s ? s.sectores : (actuador && actuatorZones && actuatorZones[actuador.actuador_id]);
                return (
                    <Fragment key={s.sensor_id}>
                        <td key={4} className="align-middle">{tipo && (tipo + extraTipo) || "-"}</td>
                        <td key={5} className="align-middle">{marca_modelo || "-"}</td>
                        <td key={6} className="align-middle">{Array.isArray(sectors) && sectors.length > 0 ? sectors.map(id =>
                            <Fragment key={id}><ButtonPrime link small raised={false} onClick={() => navigate(
                                { pathname: "/farms/zone", search: `?farm=${farmId}&zone=${id}` },
                                { state: { expanded: ["sectores",`sector-${id}`], updateTree: true } }
                            )} rel="noopener noreferrer" label={sectorsInfo && sectorsInfo[id] ? sectorsInfo[id].nombre : "-"}
                            /><br />
                            </Fragment>
                        ) : "-"}
                        </td>
                    </Fragment>
                );
            })
            :
            <>
                <td key={4} className="align-middle">{tipo && (tipo + extraTipo) || "-"}</td>
                <td key={5} className="align-middle">{marca_modelo || "-"}</td>
                <td key={6} className="align-middle">{Array.isArray(sectors) && sectors.length > 0 ? sectors.map(id =>
                    <Fragment key={id}>
                        <ButtonPrime link small raised={false} onClick={() => navigate(
                            { pathname: "/farms/zone", search: `?farm=${farmId}&zone=${id}` },
                            { state: { expanded: ["sectores",`sector-${id}`], updateTree: true } }
                        )} rel="noopener noreferrer" label={sectorsInfo && sectorsInfo[id] ? sectorsInfo[id].nombre : "-"}
                        /><br />
                    </Fragment>
                ) : "-"}
                </td>
            </>}
    </tr>);
};

const EmptyRow = ({ x1, c1, expansion, slot }) => {
    return (<tr>
        {x1 && <td>{slot}</td>}
        {x1 && <td colSpan={2}>{expansion}</td>}
        {c1 && <td>-</td>}
        {!x1 && <td>-</td>}
        <td>-</td>
        <td>-</td>
        <td>-</td>
        <td>-</td>
    </tr>);
};


const getSensor = (sensor) => Array.isArray(sensor) ? sensor[0] : sensor;

// puertos must be an array, with the index beign the port number
const addToLastPort = (children, firstInTerminal, firstInPort, common, sensor, actuador) => {
    children.push({ ...common, key: `c-${children.length}`, sensor, actuador, terminalGroup: 0, portGroup: 0 });
    firstInTerminal.terminalGroup = firstInTerminal.terminalGroup + 1;
    firstInPort.portGroup = firstInPort.portGroup + 1;
};

// add the sensors from index 1...
const addAdditionalSensors = (sensors, children, firstInTerminal, firstInPort, common) => {
    for (let i = 1; i < sensors.length; i++) {
        addToLastPort(children, firstInTerminal, firstInPort, common, sensors[i], undefined);
    }
};

// puertos must be an array, with the index beign the port number
const ExpansionRows = ({ farmId, x1, m1, c1, slot, tipo, modelo, puertos, t, lng, sectorsInfo, actuatorZones }) => {
    const navigate = useNavigate();
    const expansion = getExpansionName(t, { modelo, tipo });
    const data = Array.isArray(puertos) && mapeo[tipo];
    const children = [];
    const common = { farmId, x1, m1, c1, lng, t, sectorsInfo, actuatorZones, navigate };

    if (data) {
        const showTerminal = tipo === 12;
        let firstInTerminal = null;
        data.forEach(({ i18n, min, max, port }) => {
            const offset = min - 1;
            const groupLength = 1 + max - min;
            const portsArray = Array.apply(null, { length: 1 + groupLength });
            delete portsArray[0]; // this way with map() we start counting from 1 // eg: min: 85 & max: 86 -> indexes [1, 2]
            let firstInPort = null;
            portsArray.forEach((_, p) => {
                const { sensor, actuador } = puertos[offset + p] || {};
                if (port) { // in this case, we group all entries in the same port
                    const childrenProps = {
                        key: children.length, showTerminal, ...common, sensor: getSensor(sensor), actuador,
                        terminal: i18n, port, terminalGroup: 0, portGroup: 0
                    };
                    const hasSameTerminal = firstInTerminal && firstInTerminal.terminal === i18n;
                    const hasSamePort = hasSameTerminal && firstInPort && firstInPort.port === port;
                    if (hasSameTerminal && hasSamePort && !actuador && !sensor) return;
                    const firstIsEmpty = hasSamePort && !firstInPort.sensor && !firstInPort.actuador;

                    if (!hasSameTerminal) firstInTerminal = childrenProps;
                    if (!hasSamePort) firstInPort = childrenProps;

                    if (firstIsEmpty) {
                        firstInPort.actuador = actuador;
                        firstInPort.sensor = getSensor(sensor);
                    } else {
                        firstInTerminal.terminalGroup = firstInTerminal.terminalGroup + 1;
                        firstInPort.portGroup = firstInPort.portGroup + 1;
                        children.push(childrenProps);
                    }
                    if (Array.isArray(sensor)) addAdditionalSensors(sensor, children, firstInTerminal, firstInPort, common);
                } else {
                    if (firstInTerminal) firstInTerminal = null;
                    const thisProps = {
                        key: children.length, showTerminal, ...common, sensor: getSensor(sensor), actuador,
                        terminal: i18n, port: p, terminalGroup: p === 1 ? groupLength : 0, portGroup: 1
                    };
                    children.push(thisProps);

                    if (p === 1) firstInPort = thisProps;
                    if (Array.isArray(sensor)) addAdditionalSensors(sensor, children, firstInPort, thisProps, common);
                }
            });
            // if we group per port, but we dont have any logical port used, we add an empty port
            if (port && !firstInPort) children.push({ key: children.length, ...common, terminal: i18n, port, terminalGroup: 1, portGroup: 1 });
        });
    } else {
        let firstInTerminal = null;
        let lastIdx = 0;
        Array.isArray(puertos) && puertos.forEach(({ sensor, actuador, sist_tipo_puerto_te }, index) => {
            if (index >= 120) return;
            const i18n = `ports.raw.${sist_tipo_puerto_te}`;
            const groupPort = [2, 9, 10].includes(sist_tipo_puerto_te);

            let nHoles = (index - lastIdx) - 1;
            const childrenProps = {
                key: index, ...common, sensor: getSensor(sensor), actuador,
                terminal: i18n, port: groupPort ? 1 : index, terminalGroup: 0, portGroup: groupPort ? 0 : 1
            };
            lastIdx = index;

            const hasSameTerminal = firstInTerminal && firstInTerminal.terminal === i18n;
            if (!hasSameTerminal) {
                firstInTerminal = childrenProps;
                nHoles = 0;
            } else if (groupPort && !actuador && !sensor) return;

            const firstIsEmpty = hasSameTerminal && groupPort && !firstInTerminal.sensor && !firstInTerminal.actuador;
            if (firstIsEmpty) {
                if (sensor || actuador) {
                    firstInTerminal.actuador = actuador;
                    firstInTerminal.sensor = getSensor(sensor);
                    firstInTerminal.terminalGroup = 1;
                    firstInTerminal.portGroup = 1;
                }
                if (Array.isArray(sensor)) addAdditionalSensors(sensor, children, firstInTerminal, firstInTerminal, common);
                return;
            }

            firstInTerminal.terminalGroup = firstInTerminal.terminalGroup + 1;
            if (groupPort) {
                firstInTerminal.portGroup = firstInTerminal.portGroup + 1;
            } else if (nHoles) { // if we added port: 2, and then port: 10, we add ports: 3, 4, 5, etc
                firstInTerminal.terminalGroup = firstInTerminal.terminalGroup + nHoles;

                const holesArray = Array.apply(null, { length: nHoles + 1 });
                delete holesArray[0]; // this way with map() we start counting from 1
                holesArray.forEach((_, idx) => {
                    const key = lastIdx + idx;
                    children.push({ key, ...common, terminal: i18n, port: key, terminalGroup: 0, portGroup: 1 });
                });
            }
            children.push(childrenProps);
            if (Array.isArray(sensor)) addAdditionalSensors(sensor, children, firstInTerminal, childrenProps, common);
        });
    }

    if (!children.length) return <EmptyRow x1={x1} m1={m1} c1={c1} expansion={expansion} slot={slot} />;
    const firstChild = children[0];
    firstChild.slot = slot;
    firstChild.expansion = expansion;
    firstChild.expansionGroup = children.length;
    return <>{children.map((props, idx) => ExpansionRow(props, idx))}</>;
};

const Body = ({ farmId, x1, c1, m1, portsData, sectorsInfo, actuatorZones, t, lng }) => {
    const arr = Array.apply(null, { length: portsData.maxNPort + 1 });
    delete arr[0];
    return (
        <tbody>{arr.map((_, index) => {
            const data = portsData.hierarchy[index];
            return (data ? <ExpansionRows key={index} x1={x1} m1={m1} c1={c1} farmId={farmId} slot={index}
                tipo={data.tipo} modelo={data.modelo} puertos={data.puertos} t={t}
                sectorsInfo={sectorsInfo} actuatorZones={actuatorZones} lng={lng}
            />
                :
                <EmptyRow slot={index} x1={x1} m1={m1} c1={c1} key={index} expansion={t("txt.Vacio_LABEL")} />
            );
        })}
        </tbody >
    );
};

const Header = ({ x1, c1, m1,v1, t }) => {
    const colNames = x1 ? [t("thead.slot"), t("thead.expansion")] :
        c1? [t("thead.device"), t("thead.terminal")] :
            m1 || v1 ? [t("thead.terminal")] : [];
    const commonNames = [t("thead.puerto"), t("thead.tipo"), t("thead.marcaModelo"), t("thead.sector")];
    return (
        <thead>
            <tr>
                <>{colNames && colNames.map((name, index) => <th key={index} colSpan={(x1 && index === 1) ? 2 : 1}>{name}</th>)}</>
                <>{commonNames.map((name, index) => <th key={index}>{name}</th>)}</>
            </tr>
        </thead >
    );
};

const PortsTable = ({ farmId, x1, c1, m1,v1, sectorsInfo, actuatorZones, portsData, t, lng }) => {
    if (!portsData || !portsData.hierarchy) return null;
    return (
        <table className="tabla-puertos table table-bordered">
            <Header x1={x1} c1={c1} m1={m1} t={t} v1={v1} />
            <Body farmId={farmId} x1={x1} c1={c1} m1={m1} portsData={portsData} sectorsInfo={sectorsInfo} actuatorZones={actuatorZones} t={t} lng={lng} />
        </table>
    );
};

const formatAnswerYesNo = (t, param) => {
    if (param === true) {
        return t("general.answerYes");
    } else {
        return t("general.answerNo");
    }
};

const formatNodeName = (param) => {
    const splity = param.split("[");
    const name = splity[0];
    return name;
};

class NodeForm extends Component {
    render() {
        const { dataNodos, resetForm, status = {}, canEdit, setStatus, data, timezone, i18n, t, handleSubmit, isTableDialog } = this.props;
        if (!data) return <div className="card col-xl-12 col-lg-12 col-sm-12"> <div className="card-body altura-min">{/* No Data */}</div></div>;
        const isEdit = canEdit && status.edit;
        const dataNodo = (data && dataNodos && dataNodos[data.nodo_id]) || {};
        const arrFinalidades = data.finalidades.map(finalidad => {
            return t(`node.tipo.${finalidad}`);
        });
        const finalidades = arrFinalidades.join(", ");
        const selfHelpImg1 = import.meta.glob("../../img/autoayuda/*/autoayuda-node-1.png", { eager: true, import: "default" })[`../../img/autoayuda/${i18n.language}/autoayuda-node-1.png`];
        const selfHelpImg2 = import.meta.glob("../../img/autoayuda/*/autoayuda-node-2.png", { eager: true, import: "default" })[`../../img/autoayuda/${i18n.language}/autoayuda-node-2.png`];
        const selfHelpParams = {
            header: t("selfHelp.farmInfo.properties"),
            params: [`${t("general.name")} (${t("selfHelp.node.ifIsGwMsg")})`, t("node.latitud"), t("node.longitud")],
            definitionName: [
                `${t("node.latitud")}/${t("node.longitud")}`,
                t("node.ultimoDato"),
                t("node.tipoPlan")
            ],
            definitionText: [t("selfHelp.definitions.latLng"), t("selfHelp.definitions.lastDate"), t("selfHelp.definitions.planType")]
        };
        return (
            <div className={isTableDialog ? "" : "card"}>
                <div className={isTableDialog ? "" : "card-body altura-min"}>
                    <Form id={node} onSubmit={handleSubmit}>
                        {!isTableDialog ? <>
                            <div className='row'>
                                <div className='col col-sm-2 col-md-3 col-lg-2'>
                                    <SelfHelp t={t} params={selfHelpParams} images={[selfHelpImg1, selfHelpImg2]} lng={i18n.language} />
                                </div>
                                <div className='col col-sm-8 col-md-6 col-lg-8 text-center'>
                                    <h5 className="card-title"><span>{t("node.nodeInfo")}</span></h5>
                                </div>
                                <div className='col col-sm-2 col-md-3 col-lg-2 d-flex justify-content-end align-items-center'>
                                    <HeaderButtons edit={status.edit} canEdit={canEdit} setStatus={setStatus} resetForm={resetForm} form={node} />
                                </div>
                            </div>
                            <div className='linea-titulo' />
                        </> : null}
                        <div className='row'>
                            <div className='col-12 text-center'>
                                <FormRow
                                    edit={isEdit && !data.has_modem}
                                    dato1={data.nombre === "" || data.nombre === null ? null : formatNodeName(data.nombre)}
                                    nombre1={isEdit && !data.has_modem ? `${t("general.name")} *` : t("general.name")}
                                    campo1="nombre"
                                />

                                {!isTableDialog && data.is_gw ?
                                    <FormRow
                                        dato1={formatAnswerYesNo(t, true)}
                                        nombre1={t("node.isGw")}
                                        campo1="is_gateway"
                                    /> : null}

                                <FormRow
                                    edit={false}
                                    dato1={data.tipo === "" || data.tipo === null ? null : switchTipoNodo(data.tipo, (data?.sub_tipo_nodo?.lite?"lite":data?.modelo_id?data?.modelo_id:null))}
                                    nombre1={t("node.tipoHeader")}
                                    campo1="tipo"
                                />
                                <FormRow
                                    dato1={data.mac}
                                    nombre1="MAC"
                                    campo1="mac"
                                />
                                <FormRow
                                    edit={isEdit}
                                    dato1={t("general.number", { value: dataNodo.lat })}
                                    nombre1={t("node.latitud")}
                                    campo1="lat"
                                    type1="number" step1="any" min1={-90} max1={90}
                                    dato2={t("general.number", { value: dataNodo.lng })}
                                    nombre2={t("node.longitud")}
                                    campo2="lng"
                                    type2="number" step2="any" min2={-180} max2={180}
                                />

                                {!isTableDialog ? <>
                                    <FormRow
                                        dato1={data.nivel_bateria_porcentaje === null ? null : `${t("general.number", { value: data.nivel_bateria_porcentaje })} % `}
                                        nombre1={t("node.nivelBateria")}
                                        campo1="mac"
                                        dato2={data.fecha_ultimo_dato === "" || data.fecha_ultimo_dato === null ? "-" : dateFormat(data.fecha_ultimo_dato, t, i18n.language, timezone)}
                                        nombre2={t("node.ultimoDato")}
                                        campo2="fecha_ultimo_dato"
                                    />
                                    <FormRow
                                        dato1={data.fecha_entrega ? dateFormat(data.fecha_entrega, t, i18n.language, null, true) : null}
                                        nombre1={t("node.fechaEntrega")}
                                        campo1="fecha_entrega"
                                    />
                                    <FormRow
                                        dato1={data.plan_contrato??"FREE"}
                                        nombre1={t("node.tipoPlan")}
                                        campo1="plan_contrato"
                                    />
                                </> : null}

                                <FormRow
                                    dato1={finalidades}
                                    nombre1={t("node.finalidad")}
                                    campo1="finalidad"
                                />
                            </div>
                        </div>
                    </Form>
                </div>
            </div>
        );
    }
}

const handleSubmit = async (values, { props, ...actions }) => {
    const mapHandle = props.data && props.data.nodo_id && props.mapRef && props.mapRef.current;
    const mapValues = mapHandle && mapHandle.getMapInfo({ nodeId: props.data.nodo_id }, values);
    let mapRes = undefined;
    const valuesToPass = { ...values, nodo_id:props.data.nodo_id };
    delete valuesToPass.lat;
    delete valuesToPass.lng;

    if (mapValues && mapValues.isModified) {
        mapRes = updateMapInfo(props.farmId, mapValues, null, props.dispatch);
    }
    props.updateData({...valuesToPass, mapRes:mapRes}, props.data, "nodo_id");
    if (!props.cancel) actions.setStatus({ edit: false });
};
const FormikForm = withFormik({
    mapPropsToStatus: (props) => {
        return {
            edit: (props && props.edit) || false,
        };
    },
    mapPropsToValues: (props) => {
        const data = Object.assign({}, props.data);
        const dataNodo = (data && props.dataNodos && props.dataNodos[data.nodo_id]) || {};
        return {
            nombre: data.nombre,
            lat: dataNodo.lat,
            lng: dataNodo.lng,
        };
    },
    enableReinitialize: true,
    handleSubmit
})(withTranslation()(NodeForm));

const Node = ({ dataFarm, mapRef }) => {
    const location = useLocation();
    const nodeId = getNode(location);
    const farmId = getFarm(location);
    const { t, i18n } = useTranslation();
    const [info, setInfo] = useState({ data: null, isLoaded: false });
    const [edit, setEdit] = useState(false);
    const [_, dispatch] = useContext(FarmContext);

    const sectorsDict = dispatch({ type: "get", path: "sectorsDict" });
    const sectors = dispatch({ type: "get", path: "sectors" });
    const canEdit = dispatch({ type: "get", path: "canEdit" });

    const selfHelpImg1 = import.meta.glob("../../img/autoayuda/*/autoayuda-nodemap-1.png", { eager: true, import: "default" })[`../../img/autoayuda/${i18n.language}/autoayuda-nodemap-1.png`];
    const selfHelpImg2 = import.meta.glob("../../img/autoayuda/*/autoayuda-nodemap-2.png", { eager: true, import: "default" })[`../../img/autoayuda/${i18n.language}/autoayuda-nodemap-2.png`];
    const selfHelpImg3 = import.meta.glob("../../img/autoayuda/*/autoayuda-nodemap-3.png", { eager: true, import: "default" })[`../../img/autoayuda/${i18n.language}/autoayuda-nodemap-3.png`];
    const selfHelpImg4 = import.meta.glob("../../img/autoayuda/*/autoayuda-nodemap-4.png", { eager: true, import: "default" })[`../../img/autoayuda/${i18n.language}/autoayuda-nodemap-4.png`];

    const visibleMap = useMemo(() => {
        if (dataFarm.mapa_json && dataFarm.mapa_json.configurado) return true;
        return false;
    }, [dataFarm]);

    const actuatorZones = useMemo(() => {
        const newActuatorZones = [];
        const data = info && info.data;
        if (Array.isArray(sectors) && Array.isArray(data && data.actuadores)) data.actuadores.forEach((a) => {
            if (!a || !a.actuador_id || !Array.isArray(a.hidraulicas)) return;
            const arrSectoresRelacionados = [];
            const hArray = a.hidraulicas.reduce((res, h) => {
                if (h && h.hidraulica_id) res[h.hidraulica_id] = true;
                return res;
            }, []);

            sectors.forEach(s => {
                if (!s || !s.sector_id || !Array.isArray(s.hidraulicas)) return;
                const commonHydra = s.hidraulicas.find(h => hArray[h]);
                if (commonHydra) arrSectoresRelacionados.push(s.sector_id);
            });
            newActuatorZones[a.actuador_id] = arrSectoresRelacionados;
        });
        return newActuatorZones;
    }, [sectorsDict, info]);
    useEffect(() => {
        setInfo((state) => {
            return { ...state, isLoaded: false };
        });
        if (edit) setEdit(false);
        if (nodeId != null) {
            const param = `/farms/${farmId}/node/${nodeId}`;

            const getDatos = async () => {
                RestAPI.get("apiAdminDropControl", param)
                    .then(res => JSON.parse(JSON.stringify(res)))
                    .then(([nodeData]) => {
                        if (nodeData) {
                            if (nodeData.nombre) nodeData.nombre = nodeData.nombre.split("[")[0].trim();
                            const ports = formatPortsData(nodeData);
                            delete nodeData.puertos_tarjetas_expansion;

                            dispatch({ type: "update", path: "nodes", params: [{ ...nodeData }], skipReload: true });
                            nodeData.ports = ports;

                        }
                        setInfo({ isLoaded: true, data: nodeData });
                    })
                    .catch(err => console.error(err));
            };
            getDatos();
        }
    // this runs on initial node-page load, so only depends on the id
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [nodeId]);

    const { filterNodes } = useMemo(() => ({
        filterNodes: [nodeId]
    }), [nodeId]);

    const dataNodos = dataFarm.mapa_json && dataFarm.mapa_json.nodos;
    const dataNodo = dataNodos && dataNodos[nodeId];
    const tooltipVisible = (!edit && (!dataNodo || dataNodo.lat === undefined || dataNodo.lng === undefined)) ? true : false;

    const imgHeaderX1 = import.meta.glob("../../img/autoayuda/*/autoayuda-sensTable-x1.png", { eager: true, import: "default" })[`../../img/autoayuda/${i18n.language}/autoayuda-sensTable-x1.png`];
    const imgHeaderM1 = import.meta.glob("../../img/autoayuda/*/autoayuda-sensTable-m1.png", { eager: true, import: "default" })[`../../img/autoayuda/${i18n.language}/autoayuda-sensTable-m1.png`];
    const imgHeaderC1 = import.meta.glob("../../img/autoayuda/*/autoayuda-sensTable-c1.png", { eager: true, import: "default" })[`../../img/autoayuda/${i18n.language}/autoayuda-sensTable-c1.png`];

    const selfHelpParams2 =
    [
        {
            tab: 1, header: { text: t("selfHelp.properties") }, introText: t("selfHelp.nodeSensTable.introMsg"), params:
          [
              { id: 1, title: t("selfHelp.nodeSensTable.forX"), text: t("selfHelp.nodeSensTable.xMsgText"), image: imgHeaderX1 },
              { id: 2, title: t("selfHelp.nodeSensTable.forC"), text: t("selfHelp.nodeSensTable.cMsgText"), image: imgHeaderC1 },
              { id: 3, title: t("selfHelp.nodeSensTable.forM"), text: t("selfHelp.nodeSensTable.mMsgText"), image: imgHeaderM1 }
          ]
        }
    ];

    return (
        <div className='row-container'>
            {info.isLoaded === false && <LoadingDialog />}
            <div className='row'>
                <div className="col-xl-6 col-lg-12 col-sm-12">
                    <FormikForm
                        canEdit={canEdit}
                        dataNodos={dataNodos}
                        dispatch={dispatch}
                        mapRef={mapRef}
                        farmId={farmId}
                        timezone={dataFarm.huso_horario}
                        data={info.data} isLoaded={info.isLoaded}
                        updateData={(newData, _, mapRes) => callSend(`/farms/${farmId}/node/${nodeId}`, info.data, newData, t, (args) => {
                            setInfo(args);
                            dispatch({ type: "update", path: "nodes", params: [args.data] });
                        }, null, null, null, mapRes)}
                    />
                </div>

                <div className="col-xl-6 col-lg-12 col-sm-12 map-col">
                    <div className="card">
                        <div className="card-body">
                            <div className='row'>
                                <div className='col col-sm-2 col-md-3 col-lg-4'>
                                    <SelfHelp t={t} params={null} images={[selfHelpImg1, selfHelpImg4, selfHelpImg2, selfHelpImg3]} lng={i18n.language} nodeMap />
                                </div>
                                <div className='col col-sm-8 col-md-6 col-lg-4 text-center'>
                                    <h5 className="card-title">
                                        <span>{t("maps.map")}</span>
                                        {tooltipVisible ?
                                            <TooltipPrime id="nodo-map"  content={t("maps.nodata")} position="right">
                                                <FontAwesomeIcon size="1x" icon={faInfoCircle} style={{ color: "tomato" }} />
                                            </TooltipPrime>
                                            : null}
                                    </h5>
                                </div>
                                <div className='col col-sm-2 col-md-3 col-lg-4 d-flex justify-content-end align-items-center'>
                                    {!visibleMap && edit &&
                      <TooltipPrime id="nodo-map-no-tip"  content={t("maps.mapaNoVisibleTip")} position="bottom">
                          <FontAwesomeIcon size="lg" style={{ color: "orange", marginRight:"20px"  }} icon={faExclamationTriangle} />
                      </TooltipPrime>}
                                    <form id="node_map" onSubmit={(e) => {
                                        e.preventDefault();
                                        if (mapRef.current) {
                                            const values = mapRef.current.getMapInfo({ nodeId });
                                            updateMapInfo(farmId, values, t, dispatch, !visibleMap);
                                            setEdit(false);
                                        }
                                    }}
                                    >
                                        <HeaderButtons form="node_map" edit={edit} canEdit={canEdit} setEdit={setEdit} mapRef={mapRef.current} mapOptions={dataFarm.mapa_json} resetForm={() => null} />
                                    </form>
                                </div>
                            </div>
                            <div className='linea-titulo' />
                            <div className='mapsWrapper'>
                                {info.isLoaded &&
                  <GoogleMapsFarm
                      apiKey="AIzaSyCvs7xPDh2vfmQO2w02lcItOXa6OKOVmQ4"
                      data={dataFarm.mapa_json}
                      edit={canEdit && edit}
                      farmId={dataFarm.campo_id}
                      nodeId={nodeId}
                      filterNodes={filterNodes}
                      hideSectors
                      innerRef={mapRef}
                      language={i18n.language}
                      husoHorario={dataFarm.huso_horario}
                  />}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-12">
                    <div className="card">
                        <div className="card-body table-responsive">
                            <div className="row">
                                <div className='col col-sm-2 col-md-3 col-lg-4'>
                                    <SelfHelp t={t} lng={i18n.language} params={selfHelpParams2} nodeSensTable />
                                </div>
                                <div className='col col-sm-8 col-md-6 col-lg-4 text-center'>
                                    <h5 className="card-title text-center"><span>{t("node.portsTableName")}</span></h5>
                                </div>
                            </div>
                            <div className='linea-titulo' />
                            <PortsTable farmId={farmId} nodo={info.data && info.data}
                                x1={info.data && (info.data.tipo === 1 || info.data.tipo === 2)}
                                c1={info.data && (info.data.tipo === 7 || info.data.tipo === 8)}
                                m1={info.data && (info.data.tipo === 9 || info.data.tipo === 10)}
                                v1={info.data && (info.data.tipo === 16)}
                                sectorsInfo={sectorsDict} actuatorZones={actuatorZones}
                                portsData={info.data && info.data.ports} t={t} lng={getBaseLangs(i18n)}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export { FormikForm as NodeForm };
export default withName(Node, "NodeFrag");
export { formatPortsData };
